import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SuccessModal from "@/components/SuccessModal";
import {
  Landmark,
  Plus,
  Trash2,
  ShieldCheck,
  Loader2,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import {
  getEarningsHistory,
  listBanks,
  listBankAccounts,
  saveBankAccount,
  deleteBankAccount,
  setDefaultBank,
  resolveBankAccount,
  requestWithdrawal,
  listMyWithdrawals,
  cancelWithdrawal,
  getWallet,
} from "@/lib/afritask";

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const STATUS_META = {
  pending: { label: "Pending review", tone: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  pending_manual: { label: "Approved — awaiting bank transfer", tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: ShieldCheck },
  processing: { label: "Processing", tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Loader2 },
  paid: { label: "Paid", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  failed: { label: "Failed — refunded", tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  cancelled: { label: "Cancelled", tone: "bg-neutral-100 text-neutral-600 border-neutral-200", Icon: XCircle },
};

export default function Earnings() {
  const { me, wallet: outerWallet, refreshWallet } = useOutletContext();
  const [wallet, setWallet] = useState(outerWallet);
  const [history, setHistory] = useState([]);
  const [banks, setBanks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // account object
  const [withdrawSuccess, setWithdrawSuccess] = useState(null); // {amount, bank_masked}

  useEffect(() => setWallet(outerWallet), [outerWallet]);

  const reloadAll = async () => {
    try {
      const [h, a, w, wl] = await Promise.all([
        getEarningsHistory().catch(() => []),
        listBankAccounts().catch(() => []),
        listMyWithdrawals().catch(() => []),
        getWallet().catch(() => null),
      ]);
      setHistory(h);
      setAccounts(a);
      setWithdrawals(w);
      if (wl) setWallet(wl);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!me) return;
    reloadAll();
    listBanks().then(setBanks).catch(() => setBanks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const earnings = wallet?.earnings_ngn ?? 0;
  const lifetime = wallet?.lifetime_earned ?? 0;
  const canWithdraw = earnings >= 2000 && accounts.length > 0;
  const activeWithdrawal = withdrawals.find((w) =>
    ["pending", "pending_manual", "processing"].includes(w.status)
  );

  const handleCancel = async (id) => {
    try {
      await cancelWithdrawal(id);
      toast.success("Withdrawal cancelled — refund posted to your Earnings Wallet");
      await reloadAll();
      refreshWallet?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="space-y-6">
      {/* Balance hero */}
      <Card className="overflow-hidden">
        <div
          className="p-6 text-white"
          style={{ background: "linear-gradient(135deg, #B45309 0%, #D97706 100%)" }}
        >
          <div className="flex items-center gap-2 text-amber-50">
            <Landmark className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">
              Earnings wallet
            </span>
          </div>
          <p
            className="mt-3 text-5xl font-bold tabular-nums"
            data-testid="earnings-balance"
          >
            {fmtNaira(earnings)}
          </p>
          <p className="mt-2 text-sm text-amber-50">
            Lifetime earned:{" "}
            <span className="font-semibold text-white">{fmtNaira(lifetime)}</span>
          </p>
        </div>
        <CardContent className="flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-900">Withdraw to bank</p>
            <p className="text-xs text-neutral-500">
              Minimum withdrawal: <span className="font-semibold">₦2,000</span>
            </p>
          </div>
          <Button
            disabled={!canWithdraw || !!activeWithdrawal}
            onClick={() => setWithdrawOpen(true)}
            data-testid="earnings-withdraw-btn"
            className="rounded-lg bg-amber-700 hover:bg-amber-800"
            title={
              activeWithdrawal
                ? "You already have a pending withdrawal"
                : !canWithdraw
                ? "You need ₦2,000+ and a saved bank"
                : "Request a withdrawal"
            }
          >
            {activeWithdrawal
              ? "Withdrawal in progress"
              : !canWithdraw && earnings < 2000
              ? "Min ₦2,000 to withdraw"
              : !canWithdraw
              ? "Add a bank first"
              : "Request withdrawal"}
          </Button>
        </CardContent>
      </Card>

      {/* Active withdrawal */}
      {activeWithdrawal && (
        <ActiveWithdrawalCard
          w={activeWithdrawal}
          onCancel={() => handleCancel(activeWithdrawal.withdrawal_id)}
        />
      )}

      {/* Bank accounts */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Saved bank accounts</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            data-testid="add-bank-btn"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add bank
          </Button>
        </div>
        <Card>
          <CardContent className="p-0">
            {accounts.length === 0 && (
              <div className="py-10 text-center text-sm text-neutral-500">
                No bank accounts saved yet. Add one to withdraw your earnings.
              </div>
            )}
            {accounts.length > 0 && (
              <ul className="divide-y divide-neutral-100" data-testid="bank-accounts-list">
                {accounts.map((a) => (
                  <li
                    key={a.bank_account_id}
                    className="flex items-center gap-4 px-5 py-4"
                    data-testid={`bank-item-${a.bank_account_id}`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                      <Landmark className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-neutral-900">
                          {a.bank_name}
                        </p>
                        {a.is_default && (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            <Star className="mr-1 h-3 w-3" /> Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500">
                        {a.account_holder_name} · {a.account_number_masked}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!a.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            await setDefaultBank(a.bank_account_id);
                            toast.success("Default bank updated");
                            reloadAll();
                          }}
                          data-testid={`bank-set-default-${a.bank_account_id}`}
                        >
                          Set default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(a)}
                        data-testid={`bank-delete-${a.bank_account_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-neutral-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Withdrawal history */}
      <section>
        <h2 className="mb-3 text-base font-semibold">Withdrawal history</h2>
        <Card>
          <CardContent className="p-0">
            <Table data-testid="withdrawals-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Requested</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {withdrawals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-10 text-center text-sm text-neutral-500">
                      No withdrawals yet.
                    </TableCell>
                  </TableRow>
                )}
                {withdrawals.map((w) => {
                  const meta = STATUS_META[w.status] || STATUS_META.pending;
                  const Icon = meta.Icon;
                  return (
                    <TableRow key={w.withdrawal_id} data-testid={`withdrawal-row-${w.withdrawal_id}`}>
                      <TableCell className="text-xs text-neutral-500">
                        {fmtWhen(w.requested_at)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <p className="font-medium text-neutral-900">{w.bank_name}</p>
                        <p className="text-neutral-500">{w.account_number_masked}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`gap-1 ${meta.tone}`}>
                          <Icon className={`h-3 w-3 ${w.status === "processing" ? "animate-spin" : ""}`} />
                          {meta.label}
                        </Badge>
                        {w.rejection_reason && (
                          <p className="mt-1 text-[10px] text-red-500">{w.rejection_reason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {fmtNaira(w.amount_ngn)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      {/* Earnings history (existing) */}
      <section>
        <h2 className="mb-3 text-base font-semibold">Earnings history</h2>
        <Card>
          <CardContent className="p-0">
            <Table data-testid="earnings-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Δ (₦)</TableHead>
                  <TableHead className="text-right">Balance after</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-10 text-center text-sm text-neutral-500">
                      No earnings yet — complete a selected task and wait for approval.
                    </TableCell>
                  </TableRow>
                )}
                {history.map((e, i) => (
                  <TableRow key={`${e.ref}-${i}`}>
                    <TableCell className="text-xs text-neutral-500">
                      {fmtWhen(e.timestamp)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="font-mono">
                        {e.reason}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right font-semibold tabular-nums ${
                        e.delta_ngn >= 0 ? "text-emerald-700" : "text-red-600"
                      }`}
                    >
                      {e.delta_ngn >= 0 ? "+" : ""}
                      {fmtNaira(e.delta_ngn)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNaira(e.balance_after)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      <AddBankDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        banks={banks}
        onSaved={reloadAll}
      />

      <WithdrawDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        accounts={accounts}
        maxAmount={earnings}
        onDone={async (info) => {
          await reloadAll();
          refreshWallet?.();
          if (info) setWithdrawSuccess(info);
        }}
      />

      <SuccessModal
        open={!!withdrawSuccess}
        onOpenChange={(v) => !v && setWithdrawSuccess(null)}
        variant="clock"
        title="Withdrawal submitted"
        body={withdrawSuccess
          ? `${fmtNaira(withdrawSuccess.amount)} to ${withdrawSuccess.bank_masked} · Processing usually takes 24–48h`
          : ""}
        ctaLabel="Track withdrawal"
        testid="withdraw-success-modal"
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="bank-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this bank account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `${deleteTarget.bank_name} · ${deleteTarget.account_number_masked}`}
              <br />
              You can add it again anytime. Any in-progress withdrawal will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bank-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = deleteTarget;
                setDeleteTarget(null);
                try {
                  await deleteBankAccount(target.bank_account_id);
                  toast.success("Bank removed");
                  reloadAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed");
                }
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bank-delete-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ================================================== ActiveWithdrawalCard ===
function ActiveWithdrawalCard({ w, onCancel }) {
  const meta = STATUS_META[w.status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <Card data-testid="active-withdrawal-card" className="border-amber-200 bg-amber-50/40">
      <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
            <Icon className={`h-4 w-4 text-amber-700 ${w.status === "processing" ? "animate-spin" : ""}`} />
          </span>
          <div>
            <p className="text-sm font-semibold text-neutral-900">
              {fmtNaira(w.amount_ngn)} → {w.bank_name}
            </p>
            <p className="text-xs text-neutral-500">
              {w.account_number_masked} · {meta.label} · requested {fmtWhen(w.requested_at)}
            </p>
          </div>
        </div>
        {w.status === "pending" && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            data-testid="cancel-withdrawal-btn"
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            Cancel withdrawal
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================ AddBankDialog
function AddBankDialog({ open, onOpenChange, banks, onSaved }) {
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolved, setResolved] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [manualName, setManualName] = useState("");

  const bank = banks.find((b) => b.code === bankCode);

  const reset = () => {
    setBankCode("");
    setAccountNumber("");
    setResolved(null);
    setError("");
    setResolving(false);
    setSaving(false);
    setManualName("");
  };

  useEffect(() => {
    setResolved(null);
    setError("");
    setManualName("");
  }, [bankCode, accountNumber]);

  useEffect(() => {
    if (!bankCode || accountNumber.length !== 10) return;
    const t = setTimeout(async () => {
      setResolving(true);
      setError("");
      try {
        const r = await resolveBankAccount(accountNumber, bankCode);
        setResolved(r);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not resolve account");
        setResolved(null);
      } finally {
        setResolving(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [bankCode, accountNumber]);

  const holderName = resolved?.account_holder_name || manualName.trim();
  const canSave = !!bank && accountNumber.length === 10 && !!holderName && !resolving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await saveBankAccount({
        bank_name: bank.name,
        bank_code: bank.code,
        account_number: accountNumber,
        account_holder_name: holderName,
      });
      toast.success(`${bank.name} added`);
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save bank account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-md p-4 sm:max-w-lg sm:p-6 max-h-[85vh] overflow-y-auto"
        data-testid="add-bank-dialog"
      >
        <DialogHeader>
          <DialogTitle>Add a Nigerian bank account</DialogTitle>
          <DialogDescription>
            We verify your account name in real time via Paystack before saving.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Bank</Label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger data-testid="bank-select-trigger">
                <SelectValue placeholder="Select your bank" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {banks.map((b) => (
                  <SelectItem key={b.code} value={b.code}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Account number (10 digits)</Label>
            <Input
              inputMode="numeric"
              maxLength={10}
              placeholder="0123456789"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
              data-testid="bank-account-number-input"
            />
          </div>
          {(resolving || resolved || error) && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                error
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : resolving
                  ? "border-neutral-200 bg-neutral-50 text-neutral-600"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
              data-testid="bank-resolve-status"
            >
              {resolving && (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Verifying…
                </span>
              )}
              {resolved && !resolving && (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> {resolved.account_holder_name}
                </span>
              )}
              {error && !resolving && (
                <span>
                  {error} — enter the account holder name manually below to continue.
                </span>
              )}
            </div>
          )}
          {error && !resolving && (
            <div className="space-y-1.5">
              <Label>Account holder name</Label>
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Full name as it appears on the account"
                data-testid="bank-holder-name-manual-input"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSave || saving}
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-bank-btn"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================== WithdrawDialog
function WithdrawDialog({ open, onOpenChange, accounts, maxAmount, onDone }) {
  const defaultAcct = accounts.find((a) => a.is_default) || accounts[0];
  const [bankAcctId, setBankAcctId] = useState(defaultAcct?.bank_account_id || "");
  const [amount, setAmount] = useState("");
  const [termsOk, setTermsOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setBankAcctId(defaultAcct?.bank_account_id || "");
      setAmount(String(maxAmount));
      setTermsOk(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const amt = parseInt(amount || "0", 10);
  const invalid = !bankAcctId || amt < 2000 || amt > maxAmount || !termsOk;
  const selected = accounts.find((a) => a.bank_account_id === bankAcctId);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await requestWithdrawal(amt, bankAcctId);
      onOpenChange(false);
      onDone?.({ amount: amt, bank_masked: selected ? `${selected.bank_name} · ${selected.account_number_masked}` : "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-md p-4 sm:max-w-lg sm:p-6 max-h-[85vh] overflow-y-auto"
        data-testid="withdraw-dialog"
      >
        <DialogHeader>
          <DialogTitle>Request a withdrawal</DialogTitle>
          <DialogDescription>
            Minimum ₦2,000. The amount is frozen from your Earnings Wallet until an admin processes the request.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Send to</Label>
            <Select value={bankAcctId} onValueChange={setBankAcctId}>
              <SelectTrigger data-testid="withdraw-bank-select">
                <SelectValue placeholder="Choose a bank" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.bank_account_id} value={a.bank_account_id}>
                    {a.bank_name} · {a.account_number_masked} · {a.account_holder_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Amount (₦)</Label>
            <Input
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              data-testid="withdraw-amount-input"
            />
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>Available: {fmtNaira(maxAmount)}</span>
              <button
                type="button"
                onClick={() => setAmount(String(maxAmount))}
                className="font-medium text-emerald-700 hover:text-emerald-800"
              >
                Withdraw all
              </button>
            </div>
          </div>
          {selected && amt >= 2000 && amt <= maxAmount && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <p className="font-semibold text-emerald-900 break-words">
                {fmtNaira(amt)} <ArrowRight className="inline h-3 w-3" /> {selected.bank_name}
              </p>
              <p className="text-xs text-emerald-700 break-words">
                {selected.account_holder_name} · {selected.account_number_masked}
              </p>
            </div>
          )}
          <div className="flex items-start gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <Checkbox
              id="withdraw-terms"
              checked={termsOk}
              onCheckedChange={(v) => setTermsOk(v === true)}
              data-testid="withdraw-terms-checkbox"
              className="mt-0.5"
            />
            <Label htmlFor="withdraw-terms" className="cursor-pointer text-xs leading-snug text-neutral-600">
              I confirm the bank details are correct and understand that withdrawals are typically processed within 24 hours after admin review. Once approved, funds cannot be recalled.
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={invalid || submitting}
            onClick={handleSubmit}
            className="bg-[#B45309] text-white hover:bg-[#9A3412]"
            data-testid="confirm-withdraw-btn"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Confirm withdrawal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
